import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../../../../context/AuthContext';
import UrlConfig from '../../../../data/UrlConfig';
import { CheckCircleIcon, ArrowPathIcon, PencilIcon } from '@heroicons/react/24/solid';

const MeditationSummary = ({ onRestart, totalTime, onRevelationSaved }) => {
    const { user } = useAuth();
    const navigate = useNavigate();
    const urlConfig = UrlConfig;
    const minutes = Math.floor(totalTime / 60);

    const [revelation, setRevelation] = useState('');
    const [saveStatus, setSaveStatus] = useState('idle'); // idle, saving, saved, error
    const [errorMessage, setErrorMessage] = useState('');

    const handleSaveRevelation = async () => {
        if (!revelation.trim()) return;

        if (!user) {
            // Guardar datos de la acción pendiente en sessionStorage
            const pendingData = { revelation, totalTimeMeditated: totalTime };
            sessionStorage.setItem('manifiesta_pending_revelation', JSON.stringify(pendingData));
            
            // --- CAMBIO CLAVE: Guardar la ruta de destino para usar después del login/signup ---
            sessionStorage.setItem('redirectAfterLogin', '/herramientas/7-fases');
            
            // Redirigir a la página de login, desde donde puede ir al signup
            navigate('/login');
            return;
        }

        setSaveStatus('saving');
        setErrorMessage('');
        try {
            const response = await fetch(urlConfig.meditation_saveRevelation, {
                method: 'POST',
                credentials: 'include',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ revelation, totalTimeMeditated: totalTime }),
            });

            if (!response.ok) {
                const errorData = await response.json();
                throw new Error(errorData.message || 'No se pudo guardar la revelación.');
            }
            
            setSaveStatus('saved');
            if(onRevelationSaved) onRevelationSaved();

        } catch (error) {
            setSaveStatus('error');
            setErrorMessage(error.message);
        }
    };

    return (
        <div className="bg-gray-900/50 p-8 rounded-xl border border-green-500/30 text-center animate-fade-in max-w-2xl mx-auto">
            <CheckCircleIcon className="w-20 h-20 text-green-400 mx-auto mb-4" />
            <h2 className="text-4xl font-bold mb-4">¡Meditación Completada!</h2>
            <p className="text-gray-300 mb-8 text-lg">
                Dedicaste <span className="text-yellow-400 font-bold">{minutes} minutos</span> a tu bienestar. ¿Alguna revelación?
            </p>
            
            <div className="mb-10 text-left">
                <label htmlFor="revelation" className="flex items-center gap-2 text-xl font-semibold text-yellow-400 mb-3">
                    <PencilIcon className="w-6 h-6"/>
                    Anota tus Revelaciones
                </label>
                <textarea
                    id="revelation"
                    rows="4"
                    value={revelation}
                    onChange={(e) => setRevelation(e.target.value)}
                    placeholder="Escribe aquí cualquier pensamiento, imagen o sentimiento..."
                    className="w-full p-3 bg-gray-800/80 border border-gray-600 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-yellow-500 disabled:opacity-50"
                    disabled={saveStatus === 'saving' || saveStatus === 'saved'}
                />
                
                {saveStatus !== 'saved' && (
                    <button
                        onClick={handleSaveRevelation}
                        disabled={!revelation.trim() || saveStatus === 'saving'}
                        className="mt-4 w-full bg-yellow-600 text-white font-bold py-3 px-6 rounded-lg hover:bg-yellow-500 transition-all disabled:bg-gray-600 disabled:cursor-not-allowed"
                    >
                        {saveStatus === 'saving' ? 'Guardando...' : (user ? 'Guardar Revelación' : 'Guardar y Continuar')}
                    </button>
                )}

                {saveStatus === 'saved' && <p className="text-green-400 text-center mt-4 font-semibold">¡Guardado!</p>}
                {saveStatus === 'error' && <p className="text-red-400 text-center mt-4">{errorMessage}</p>}
            </div>

            <div className="flex flex-col sm:flex-row justify-center items-center gap-4">
                <button onClick={onRestart} className="flex items-center justify-center gap-2 w-full sm:w-auto bg-purple-600 text-white font-bold py-3 px-8 rounded-lg hover:bg-purple-700">
                    <ArrowPathIcon className="w-5 h-5"/>
                    Meditar de Nuevo
                </button>
                <Link to="/cursos/manifiesta" className="w-full sm:w-auto text-center bg-gray-700 text-gray-300 font-bold py-3 px-8 rounded-lg hover:bg-gray-600">
                    Volver al Temario
                </Link>
            </div>
        </div>
    );
};

export default MeditationSummary;